import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtEUR } from "@/lib/api";
import { ChevronLeft, FileDown, Truck, Package, CheckCircle2, Clock } from "lucide-react";

const STAGES = [
    { id: "pending", label: "Pagamento", icon: Clock },
    { id: "paid", label: "Pago", icon: CheckCircle2 },
    { id: "shipped", label: "Em envio", icon: Truck },
    { id: "delivered", label: "Entregue", icon: Package },
];

export default function CustomerOrderDetail() {
    const { id } = useParams();
    const [order, setOrder] = useState(null);
    useEffect(() => { api.get(`/me/orders/${id}`).then((r) => setOrder(r.data)); }, [id]);
    if (!order) return <div className="container-edge py-20 font-mono">A carregar…</div>;

    const stageIdx = order.payment_status === "paid"
        ? (order.tracking_code ? 2 : 1)
        : 0;

    const downloadInvoice = () => {
        api.get(`/me/orders/${id}/invoice.pdf`, { responseType: "blob" }).then((res) => {
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `fatura-${order.order_number}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        });
    };

    return (
        <div className="container-edge py-12 max-w-4xl" data-testid="customer-order-detail">
            <Link to="/conta" className="btn-ghost text-xs mb-4">
                <ChevronLeft size={14} /> Minha conta
            </Link>
            <div className="border-2 border-ink bg-white p-8">
                <div className="label-mono text-magenta">Encomenda</div>
                <h1 className="font-display font-black text-4xl tracking-tighter mt-2">{order.order_number}</h1>
                <div className="font-mono text-sm text-ink/60 mt-2">
                    {new Date(order.created_at).toLocaleString("pt-PT")} · Total {fmtEUR(order.total)}
                </div>

                {/* progress */}
                <div className="mt-8 grid grid-cols-4 gap-0 border-2 border-ink">
                    {STAGES.map((s, i) => {
                        const Icon = s.icon;
                        const active = i <= stageIdx;
                        return (
                            <div
                                key={s.id}
                                className={`p-4 text-center ${i < 3 ? "border-r-2 border-ink" : ""} ${
                                    active ? "bg-magenta text-white" : "bg-paper"
                                }`}
                            >
                                <Icon size={20} className="mx-auto" />
                                <div className="label-mono mt-2">{s.label}</div>
                            </div>
                        );
                    })}
                </div>

                {order.tracking_code && (
                    <div className="mt-6 border-2 border-ink p-4 flex items-center justify-between">
                        <div>
                            <div className="label-mono">Tracking ({order.shipping_carrier})</div>
                            <div className="font-mono text-lg mt-1">{order.tracking_code}</div>
                        </div>
                        <Truck className="text-magenta" size={24} />
                    </div>
                )}
            </div>

            {/* items */}
            <div className="mt-6 border-2 border-ink bg-white">
                <div className="p-5 border-b-2 border-ink">
                    <h2 className="font-display font-black text-xl">Artigos ({order.items.length})</h2>
                </div>
                {order.items.map((it, i) => (
                    <div key={it.sku} className={`flex items-center justify-between p-5 ${i < order.items.length - 1 ? "border-b border-ink/10" : ""}`}>
                        <div>
                            <div className="font-bold">{it.name}</div>
                            <div className="text-xs font-mono text-ink/60">SKU {it.sku} · {it.quantity} × {fmtEUR(it.price)}</div>
                        </div>
                        <div className="font-mono">{fmtEUR(it.line_total)}</div>
                    </div>
                ))}
                <div className="p-5 bg-paper border-t-2 border-ink space-y-1 font-mono text-sm">
                    <div className="flex justify-between"><span>Subtotal</span><span>{fmtEUR(order.subtotal)}</span></div>
                    <div className="flex justify-between"><span>Portes</span><span>{fmtEUR(order.shipping_cost)}</span></div>
                    <div className="flex justify-between font-display font-black text-xl pt-2 border-t-2 border-ink mt-2">
                        <span>Total</span><span>{fmtEUR(order.total)}</span>
                    </div>
                </div>
            </div>

            <button onClick={downloadInvoice} className="btn-primary mt-6" data-testid="download-invoice-btn">
                <FileDown size={16} /> Descarregar Fatura PDF
            </button>

            {order.payment_status === "pending" && order.payment_method === "multibanco" && order.payment_reference && (
                <div className="mt-6 border-2 border-magenta bg-magenta/5 p-6">
                    <div className="label-mono text-magenta">Referência Multibanco</div>
                    <div className="grid sm:grid-cols-3 gap-4 mt-4 font-mono">
                        <div>
                            <div className="label-mono">Entidade</div>
                            <div className="text-2xl font-bold mt-1">{order.payment_reference.entity}</div>
                        </div>
                        <div>
                            <div className="label-mono">Referência</div>
                            <div className="text-2xl font-bold mt-1">{order.payment_reference.reference}</div>
                        </div>
                        <div>
                            <div className="label-mono">Montante</div>
                            <div className="text-2xl font-bold mt-1">{fmtEUR(order.payment_reference.amount)}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
